import Value, { getValue, setValue } from "aldoo-ra/Value"
import { useEffect, useState } from "react"
import AldooStoreAPI from "aldoo-ra/Store/api"
import { priceDisplayFormat } from "aldoo-ra/CMSModels/StorePrice/display-formatters"
import StoreRender from "aldoo-ra/Store/store-render"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import PubSub from "pubsub-js"
import Modal from "aldoo-ra/Modal"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { api } from "aldoo-ra/API"
import { loadFlows, createFlowInstance } from "aldoo-ra/Store/StorePurchaseFlow"
import { getStripePromise } from "aldoo-ra/Store/stripe-client"

let storeInstance = null
let flowsLoaded = false

// ── GA4 helpers ───────────────────────────────────────────────────────────────
// Extract the effective unit price from a cart item (handles price_model phases)
const getItemPrice = (item) => {
  const priceModel = item.price_model?.find(pm => pm.primary)
  const phases = priceModel?.phases
  const introPhase = phases?.find(p => p.phase_type === 'introductory' && p.enabled !== false)
  const regularPhase = phases?.find(p => p.phase_type === 'regular')
  if (priceModel?.type === 'real') {
    return introPhase?.amount ?? regularPhase?.amount ?? priceModel?.amount ?? 0
  }
  return item.amount ?? item.price ?? 0
}

// Build a GA4-spec item object from a cart item
const buildGA4Item = (item) => ({
  item_id:   item.pid || item.product_id || item.id,
  item_name: item.name || item.pid || item.product_id,
  price:     getItemPrice(item),
  quantity:  item.quantity || 1,
})

/**
 * This component is used to handle the purchase product lifecycle.
 * It aim to maintain compatibility with the purchase flow introduced in google analytics
 * Session Start - the user
 * View Product -
 * Add To Cart -
 * Begin Checkout -
 * Purchase
 */
export default function AldooStore({ flow = 'default' }) {
  const [purchaseFlow, setPurchaseFlow] = useState(null)

  // Load flows on mount
  useEffect(() => {
    const initializeFlows = async () => {
      if (!flowsLoaded) {
        await loadFlows()
        flowsLoaded = true
      }
      // Create flow instance
      const flowInstance = createFlowInstance(flow)
      setPurchaseFlow(flowInstance)
    }

    initializeFlows()
    // Prefetch Stripe key early so it's ready before checkout opens
    getStripePromise()
  }, [flow])

  //This is the Aldoo Store cart that holds the products
  const [cart, setCart] = Value({
    key: "aldoo-store-cart",
    defaultValue: [],
    persistKey: "aldoo-store-cart",
  })
  //This is the Aldoo Store payment info that holds the payment details
  //Like a receipt in the shopping cart so the success screen can model its message
  const [paymentInfo, setPaymentInfo] = Value({
    key: "aldoo-payment-info",
    defaultValue: null,
    persistKey: "aldoo-payment-info",
  })


  //Preloaded invoice data (for users with existing billing details)
  const [preloadedInvoiceData, setPreloadedInvoiceData] = Value({
    key: "aldoo-preloaded-invoice-data",
    defaultValue: null,
  })

  //Get the auth state
  const [auth, setAuth] = PlatformAuth.useAuth()

  const [promoCodeInfo, setPromoCodeInfo] = Value({
    key: "aldoo-promo-code-info",
    defaultValue: null,
    persistKey: "aldoo-promo-code-info",
  })

  const [promoCode, setPromoCode] = Value({
    key: "aldoo-promo-code",
    defaultValue: "",
    persistKey: "aldoo-promo-code",
  })

  //Once a payment is successful, we store the payment info here
  //Since the original payment info is cleared
  //The receipt holds: { paymentInfo, cart }
  const [paymentReceipt, setPaymentReceipt] = Value({
    key: "aldoo-payment-receipt",
    defaultValue: null,
    persistKey: "aldoo-payment-receipt",
  })

  const [cartVisible, setCartVisible] = useState(false)
  const [emailDialogVisible, setEmailDialogVisible] = useState(false)
  const [checkoutVisible, setCheckoutVisible] = useState(false)
  const [paymentSuccessVisible, setPaymentSuccessVisible] = useState(false)
  const [paymentError, setPaymentError] = useState(null)
  const [promoCodeError, setPromoCodeError] = useState(null)

  // Track if this purchase will need email verification after completion
  const [purchaseRequiresVerification, setPurchaseRequiresVerification] = useState(false)
  const [purchaseUserEmail, setPurchaseUserEmail] = useState(null)
  const [purchaseUserCredentials, setPurchaseUserCredentials] = useState(null)

  // Store temporary auth for existing users (doesn't log them into the app)
  const [tempAuthForPurchase, setTempAuthForPurchase] = useState(null)

  // Use Value for in-memory storage without persistence (no key = no localStorage)
  const [paymentIntentId, setPaymentIntentId] = Value({ defaultValue: null })
  const [subscriptionId, setSubscriptionId] = Value({ defaultValue: null })

  //When the cart changes, the checkout should reinitialize a payment
  //Otherwise, the cart is just for viewing
  const [cartChanged, setCartChanged] = useState(false)

  useEffect(() => {
    //there is no paymentInfo or the cart hasn't changed
    if (!cartChanged) return
    // Clean up old subscription
    cleanupOldSubscription()
    // Clear existing payment info as it's no longer valid
    setPaymentInfo(null)
  }, [cartChanged])

  // Listen for add-to-cart-and-show event from PromoBanner
  useEffect(() => {
    const addToken = PubSub.subscribe('add-to-cart-and-show', (msg, data) => {
      if (data.product) {
        addToCart({
          product: data.product,
          proceedToCart: true
        })
      }
    })
    const removeToken = PubSub.subscribe('remove-from-cart', (msg, data) => {
      if (!data?.pid) return
      const removedItem = cart.find(item => item.pid === data.pid)
      const updatedCart = cart.filter(item => item.pid !== data.pid)
      setCart(updatedCart)
      if (removedItem) {
        const unitPrice = getItemPrice(removedItem)
        PubSub.publish('Tracking_RemoveFromCart', {
          currency: removedItem.currency,
          value: unitPrice * (removedItem.quantity || 1),
          items: [buildGA4Item(removedItem)],
        })
      }
    })
    const discountToken = PubSub.subscribe('STORE_APPLY_DISCOUNT', async (msg, data) => {
      if (!data?.code) return
      const code = data.code.toUpperCase()
      // Use module-level setValue directly — avoids stale-closure issues with the Value hook wrapper
      setValue('aldoo-promo-code', code, 'aldoo-promo-code')
      setPromoCodeError(null)
      setCartChanged(true)
      setCartVisible(true)
      // Validate and apply the code immediately so the discounted price shows on open
      const response = await AldooStoreAPI.getPromoCodeInfo(code)
      if (response.error) {
        const err = response.error === 'coupon_already_used' && response.reason
          ? response.reason
          : response.error
        setPromoCodeError(err)
        return
      }
      if (response.result === true) {
        setPromoCodeError(null)
        setValue('aldoo-promo-code-info', response, 'aldoo-promo-code-info')
      }
    })
    return () => {
      PubSub.unsubscribe(addToken)
      PubSub.unsubscribe(removeToken)
      PubSub.unsubscribe(discountToken)
    }
  }, [])

  //on free purchases announce the payment success immediately
  useEffect(() => {
    if (paymentInfo && !paymentInfo.requiresPayment) onPaymentSuccess()
  }, [paymentInfo])

  //Upon new receipt , show the success screen
  useEffect(() => {
    if (!paymentReceipt || !paymentInfo) return

    // Capture promo state before emptyCart clears it
    const appliedCoupon = getValue('aldoo-promo-code') || undefined
    const savedPromoInfo = getValue('aldoo-promo-code-info')

    //empty the cart & reset paymentInfo
    emptyCart()
    //show the payment success screen
    setPaymentSuccessVisible(true)
    // Announce payment success globally
    PubSub.publish("payment-success", paymentReceipt)

    // Track purchase with actual paid amount (post-discount)
    const receiptCart = paymentReceipt.cart || []
    if (receiptCart.length) {
      const items = receiptCart.map(buildGA4Item)
      const rawValue = items.reduce((s, i) => s + i.price * i.quantity, 0)
      const value = calculateDiscountedTotal(rawValue, savedPromoInfo)
      PubSub.publish('Tracking_Purchase', {
        currency: receiptCart[0]?.currency,
        value,
        items,
        transactionId: paymentReceipt.paymentIntentId || paymentReceipt.subscriptionId,
        coupon: appliedCoupon,
      })
    }
  }, [paymentReceipt, paymentInfo])


  const cleanupOldSubscription = async () => {
    const { subscriptionId } = paymentInfo || {}

    if (!subscriptionId) return

    try {
      // Call the API endpoint that will invoke the server-side method
      const response = await AldooStoreAPI.cancelIncompleteSubscription(
        subscriptionId
      )
      // if (response.result) {
      //   console.log("Old subscription cancelled successfully")
      // }

      // if (response.error) {
      //   console.error("Error cancelling old subscription:", response.error)
      // }
    } catch (error) {
      // console.error("Error cleaning up old subscription:", error)
    }
  }

  /**
   * Get an element for rendering from the project or the default elements
   * @param {*} name
   */
  const getElement = (name) => {
    return StoreRender.getElement(name)
  }

  const getFragment = (name) => {
    return StoreRender.getFragment(name)
  }

  const refreshCart = () => {
    setCart([...storeInstance.cart])
  }

  const emptyCart = () => {
    setCart([])
    setPaymentInfo(null)
    setPaymentError(null)
    resetPromoCode()
    //mark as changed
    setCartChanged(true)
  }

  const showCart = () => {
    //reset the cart changed flag
    setCartChanged(false)
    //clear promo code error for new checkout session
    setPromoCodeError(null)
    setCartVisible(true)
  }

  const hideCart = () => {
    setCartVisible(false)
  }

  const showCheckout = () => {
    setCheckoutVisible(true)
  }

  const resetPromoCode = () => {
    setPromoCode("")
    setPromoCodeError(null)
    setPromoCodeInfo(null)
    //mark as changed
    setCartChanged(true)
  }

  const hideCheckout = () => {
    setCheckoutVisible(false)
  }

  /**
   * Handle email verification and user setup before checkout
   */
  const handleEmailVerification = async (email) => {
    const { RequestInternalID, CreateUser, VerifyCredentials, Login } = await import("aldoo-ra/PlatformAuth/api")
    const ensureDevice = (await import("aldoo-ra/PlatformAuth/actions/ensure-device")).default
    const awaitAuth = (await import("aldoo-ra/PlatformAuth/actions/await-auth")).default
    const { api } = await import("aldoo-ra/API")

    // Use ensureDevice to get the device (same logic as signin flow)
    // This ensures we use the same fingerprint-matched device
    const deviceID = await ensureDevice({ email })
    const credentials = { email, deviceID }

    // Check if email belongs to existing user
    const { result: emailExists, _id: existingUserId } = await VerifyCredentials(credentials)

    if (emailExists && existingUserId) {
      // Email belongs to existing user
      setPurchaseRequiresVerification(true)
      setPurchaseUserEmail(email)
      setPurchaseUserCredentials(credentials)

      // Always call safe_logout first to ensure clean state (unless already logged in with same credentials)
      if (!auth || auth.email !== email || auth.deviceID !== deviceID) {
        const userAPI = api("user")
        await userAPI({
          safe_logout: { email, deviceID }
        })
      }

      // Trigger Login API - this will send magic link if device not logged in
      const loginResponse = await Login(credentials, { fetch_user_fields: ["_id"] })

      // If device already logged in, proceed immediately
      if (loginResponse.result) {

        // Log the user into the app properly
        setAuth({
          email,
          deviceID,
          auth_token: loginResponse.auth_token,
          _id: loginResponse.user._id
        })

        const tempAuth = {
          email,
          deviceID,
          auth_token: loginResponse.auth_token,
          _id: loginResponse.user._id
        }
        setTempAuthForPurchase(tempAuth)

        // Load billing details for existing user
        try {
          const invoiceData = await AldooStoreAPI.loadBillingDetails(tempAuth)
          setPreloadedInvoiceData(invoiceData)
        } catch (error) {
          setPreloadedInvoiceData(null)
        }

        // Close email dialog and continue
        setEmailDialogVisible(false)
        setPurchaseRequiresVerification(false)

        if (!checkoutVisible) {
          proceedToCheckout()
        } else {
          setCheckoutVisible(true)
        }
      } else if (loginResponse.error === "device_not_logged") {
        // Device not logged in - magic link sent, need to wait for verification
        // Return early - waitForVerification will be called separately
        return { requiresVerification: true, credentials }
      } else {
        // Other error - handle as needed
        setPurchaseRequiresVerification(false)
        setEmailDialogVisible(false)
      }
    } else {
      // Email is new - create user, log out device, then require magic link verification
      const internal_id = await RequestInternalID({ deviceID })
      await CreateUser({ deviceID, internal_id, email })

      // CreateUser authenticates the device automatically — log it out so Login() sends a magic link
      const userAPI = api("user")
      await userAPI({ safe_logout: { email, deviceID } })

      // Trigger Login to send magic link
      const loginResponse = await Login(credentials, { fetch_user_fields: ["_id"] })

      if (loginResponse.error === "device_not_logged") {
        return { requiresVerification: true, credentials }
      }

      // Edge case: device somehow already logged in — proceed directly
      if (loginResponse.result) {
        setAuth({
          email,
          deviceID,
          auth_token: loginResponse.auth_token,
          _id: loginResponse.user._id
        })
        setEmailDialogVisible(false)
        if (!checkoutVisible) {
          proceedToCheckout()
        } else {
          setCheckoutVisible(true)
        }
      }
    }

    return { requiresVerification: false }
  }

  /**
   * Wait for email verification via magic link
   */
  const waitForVerification = async (credentials) => {
    const awaitAuth = (await import("aldoo-ra/PlatformAuth/actions/await-auth")).default
    const { VerifyCredentials } = await import("aldoo-ra/PlatformAuth/api")

    // Wait for user to click magic link (5 minute timeout)
    const awaitResult = await awaitAuth(credentials)

    // Verification completed
    if (awaitResult.login) {
      // Get user ID
      const { _id } = await VerifyCredentials(credentials)

      // Log the user into the app properly
      setAuth({
        email: credentials.email,
        deviceID: credentials.deviceID,
        auth_token: awaitResult.auth_token,
        _id
      })

      // Create temp auth for API calls
      const tempAuth = {
        email: credentials.email,
        deviceID: credentials.deviceID,
        auth_token: awaitResult.auth_token,
        _id
      }
      setTempAuthForPurchase(tempAuth)

      // Load billing details for existing user
      try {
        const invoiceData = await AldooStoreAPI.loadBillingDetails(tempAuth)
        setPreloadedInvoiceData(invoiceData)
      } catch (error) {
        setPreloadedInvoiceData(null)
      }

      // Clear verification state
      setPurchaseRequiresVerification(false)
      setEmailDialogVisible(false)

      // Continue to checkout
      if (!checkoutVisible) {
        proceedToCheckout()
      } else {
        setCheckoutVisible(true)
      }

      return true
    }

    // Verification failed or timed out
    setPurchaseRequiresVerification(false)
    return false
  }

  /**
   * Proceed to checkout (after auth is set up)
   */
  const proceedToCheckout = async () => {
    // Check for billing details before showing checkout (only for logged in users)
    if (auth) {
      try {
        const invoiceData = await AldooStoreAPI.loadBillingDetails()
        setPreloadedInvoiceData(invoiceData)
      } catch (error) {
        setPreloadedInvoiceData(null)
      }
    }

    setCheckoutVisible(true)
  }

  const beginCheckout = async () => {
    setPaymentReceipt(null)
    setCartVisible(false)

    // Track begin_checkout
    if (cart.length) {
      const items = cart.map(buildGA4Item)
      const rawValue = items.reduce((s, i) => s + i.price * i.quantity, 0)
      const coupon = getValue('aldoo-promo-code') || undefined
      const savedPromoInfo = getValue('aldoo-promo-code-info')
      const value = calculateDiscountedTotal(rawValue, savedPromoInfo)
      PubSub.publish('Tracking_BeginCheckout', {
        currency: cart[0]?.currency,
        value,
        items,
        coupon,
      })
    }

    // Check for billing details before showing checkout (only for logged in users)
    let loadedInvoiceData = null
    if (auth) {
      try {
        loadedInvoiceData = await AldooStoreAPI.loadBillingDetails()
      } catch (error) {
        console.error('Error loading billing details:', error)
        // Silently handle error - user will see invoice step
      }
    }

    // Set the state with loaded data (or null)
    setPreloadedInvoiceData(loadedInvoiceData)

    // Wait for next tick to ensure state has updated
    await new Promise(resolve => setTimeout(resolve, 0))

    // Show checkout - the PurchaseFlowController will initialize the flow
    setCheckoutVisible(true)
  }

  /**
   * Add the product interface to the product object
   */
  const addProductInterface = (product) => {
    //remove one instance of the product from the cart
    product.remove = () => {
      //if the quantity is > 1, decrement the quantity
      if (product.quantity === 1) return
      //remove from the cart
      product.quantity -= 1
      refreshCart()
    }
    product.add = () => {
      //increment the quantity
      product.quantity += 1
      refreshCart()
    }
    //remove the product from the cart completely
    product.clear = () => {
      //remove from the cart
      const updatedCart = cart.filter(
        (item) => item.product_id !== product.product_id
      )
      setCart(updatedCart)
    }
  }

  /**
   * Add a product to the cart
   * @param {*} product  An object representing the product to be added to the cart
   * @param {*} proceedToCart  A boolean indicating whether to show the cart after adding the product
   * this object should have the following properties:
   * - id: the product id - this is the stable identifier for the product
   * - store_id: this is the product id as set in the Web store ( ex: Stripe, Shopify, etc)
   * - name: the product name
   * - price: the product price
   * - currency: the currency of the product
   * - quantity: the quantity of the product
   * - variant: the variant of the product
   */
  const addToCart = ({ product, proceedToCart = false }) => {
    //TODO: we support a single product purchase in version 1
    //empty the cart
    while (cart.length) cart.pop()

    //check if the product is already in the cart and increment the quantity
    const existingProduct = cart.find(
      (item) => item.product_id === product.product_id
    )
    if (existingProduct) {
      existingProduct.quantity += 1
      //make sure the product has the interface
      addProductInterface(existingProduct)
      //mark as changed
      setCartChanged(true)
      //refresh the cart
      refreshCart()
      if (proceedToCart) showCart()
      return
    }
    //add the product interface to the product
    addProductInterface(product)
    //set the initial quantity to 1
    product.quantity = 1
    //add the product to the cart
    cart.push(product)
    setCart([...cart])
    //mark as changed
    setCartChanged(true)

    // Track add_to_cart (always 1 unit added)
    const unitPrice = getItemPrice(product)
    PubSub.publish('Tracking_AddToCart', {
      currency: product.currency,
      value: unitPrice,
      items: [{ ...buildGA4Item(product), quantity: 1 }],
    })

    //if proceedToCart is true, show the cart
    if (proceedToCart) showCart()
  }

  const applyPromoCode = async () => {
    //mark as changed
    setCartChanged(true)

    //validate the promo code
    const response = await AldooStoreAPI.getPromoCodeInfo(promoCode)

    // Handle API errors
    if (response.error) {
      // For "coupon_already_used", use the more specific reason if available
      if (response.error === "coupon_already_used" && response.reason) {
        setPromoCodeError(response.reason) // "max_redemptions_reached" or "single_use_code"
      } else {
        setPromoCodeError(response.error) // "promo_code_not_found" or "coupon_expired"
      }
      return
    }

    // Handle success response (when server returns { result: true, ...promo_info })
    if (response.result === true) {
      setPromoCodeError(null)
      // The response already contains the promo code info along with result: true
      setPromoCodeInfo(response)
      return
    }

    // Legacy status check (in case old API format is still used)
    if (response.status && response.status !== "valid") {
      setPromoCodeError(response.status)
      return
    }

    //set the promo code error to null
    setPromoCodeError(null)
    //set the promo code details
    setPromoCodeInfo(response)
  }

  // Client-side discount calculation
  const calculateDiscountedTotal = (total, promoCodeInfo) => {
    // Return original total if no promo code or there's an error
    if (!promoCodeInfo || promoCodeInfo.status !== "valid") {
      return total
    }

    // Check if purchase meets minimum amount requirement
    if (promoCodeInfo.minimumAmount && total < promoCodeInfo.minimumAmount) {
      return total
    }

    let discountedTotal = total

    // Use the type and amount fields instead of direct percentOff/amountOff
    switch (promoCodeInfo.type) {
      case "percentage": {
        // Convert to cents, calculate discount, convert back to dollars
        // This ensures proper rounding for percentage discounts
        const totalInCents = Math.round(total * 100)
        const discountInCents = Math.round(totalInCents * (promoCodeInfo.amount / 100))
        const discountAmount = discountInCents / 100
        discountedTotal = total - discountAmount
        break
      }
      case "fixed": {
        // For fixed amount, directly subtract the discount
        discountedTotal = Math.max(0, total - promoCodeInfo.amount)
        break
      }
      default:
        return total
    }

    // Ensure we never return a negative amount
    return Math.max(0, discountedTotal)
  }

  const getCartTotalValue = () => {
    //if the cart is empty, return 0.00
    if (!cart.length) return "0.00"

    let total = 0
    cart.forEach((product) => {
      // Check if product has introductory pricing
      const priceModel = product.price_model?.find(pm => pm.primary)
      const phases = priceModel?.phases
      const introPhase = phases?.find(p => p.phase_type === 'introductory' && p.enabled !== false)
      const regularPhase = phases?.find(p => p.phase_type === 'regular')

      // For real currency products, always use price_model phases
      // For virtual currency products, use product.amount
      let price
      if (priceModel?.type === 'real') {
        // Real currency: use intro or regular phase amount, fallback to priceModel.amount if no phases
        price = introPhase ? introPhase.amount : (regularPhase?.amount || priceModel?.amount)
      } else {
        // Virtual currency: use product.amount
        price = product.amount
      }

      total += price * product.quantity
    })

    total = calculateDiscountedTotal(total, promoCodeInfo)

    return priceDisplayFormat(total, cart[0]?.currency)
  }

  const getPromoCodeInfo = () => {
    if (!promoCodeInfo || promoCodeError) return ""

    const {
      promoCodeId,
      duration,
      durationInMonths = null,
      percentOff,
      amountOff,
      currency,
    } = promoCodeInfo

    if (!promoCodeId) return ""

    if (percentOff) return ` -${percentOff}%`
    if (amountOff) return ` -${priceDisplayFormat(amountOff, currency)}`

    return promoCodeInfo
  }

  /**
   * Here we are in a successful payment state
   */
  const onPaymentSuccess = async (params) => {
    hideCheckout()

    // Use captured IDs from parameters if provided, otherwise fall back to state
    const capturedPaymentIntentId = params?.paymentIntentId
    const capturedSubscriptionId = params?.subscriptionId
    const finalPaymentIntentId = capturedPaymentIntentId || paymentIntentId
    const finalSubscriptionId = capturedSubscriptionId || subscriptionId

    //Set the info for the payment success screen
    //create copies of the cart and paymentInfo
    setPaymentReceipt({
      cart: [...cart],
      paymentInfo: { ...paymentInfo },
      paymentIntentId: finalPaymentIntentId,
      subscriptionId: finalSubscriptionId,
      requiresVerification: purchaseRequiresVerification,
      existingUserEmail: purchaseUserEmail,
      existingUserCredentials: purchaseUserCredentials,
    })

    // Refresh user profile to get updated account_type and other profile data
    await PlatformAuth.refreshProfile()
  }

  /**
   * An error means that the payment has failed
   * @param {*} error
   */
  const onPaymentError = (error) => {
    setPaymentError(error)
    logPaymentError(error)
  }

  const logPaymentError = (error) => {
    //Here we report the error to AldooAnalytics so we can track
    //what is the reason behind the payment failure
  }

  /**
   * The payment has succeeded but there are warnings
   * such as the payment method being expiring soon
   * or it wasn't attached properly
   * @param {*} warning
   */
  const onPaymentWarning = (warning) => {
    logPaymentError(warning)
  }

  /**
   * Get the current flow context for navigation
   */
  const getFlowContext = () => {
    // Read the latest value directly from Value store to avoid stale closure
    const latestInvoiceData = getValue("aldoo-preloaded-invoice-data")
    return {
      auth,
      tempAuthForPurchase,
      purchaseUserEmail,
      paymentInfo,
      cart,
      promoCode,
      invoiceData: latestInvoiceData,
    }
  }

  /**
   * Navigate to the next step in the flow
   */
  const goToNextStep = () => {
    if (!purchaseFlow) return null
    return purchaseFlow.goToNext(getFlowContext())
  }

  /**
   * Navigate to the previous step in the flow
   */
  const goToPreviousStep = () => {
    if (!purchaseFlow) return null
    return purchaseFlow.goBack(getFlowContext())
  }

  /**
   * Get the current step in the flow
   */
  const getCurrentFlowStep = () => {
    if (!purchaseFlow) return null
    return purchaseFlow.getCurrentStep()
  }

  //Get the render elements
  const Cart = getElement("shopping-cart")
  const Checkout = getElement("checkout")
  const PaymentSuccess = getElement("payment-success")
  const PaymentError = getElement("payment-error")
  const EmailInputDialog = getElement("email-input-dialog")

  //export the store interface
  storeInstance = {
    cartChanged,
    onPaymentSuccess,
    onPaymentError,
    logPaymentError,
    onPaymentWarning,
    cartVisible,
    emailDialogVisible,
    setEmailDialogVisible,
    handleEmailVerification,
    waitForVerification,
    checkoutVisible,
    hideCheckout,
    showCheckout,
    showCart,
    hideCart,
    setCart,
    cart,
    promoCode,
    beginCheckout,
    getElement,
    getFragment,
    setPromoCode,
    resetPromoCode,
    refreshCart,
    emptyCart,
    addToCart,
    paymentSuccessVisible,
    setPaymentSuccessVisible,
    applyPromoCode,
    getPromoCodeInfo,
    promoCodeError,
    setPromoCodeError,
    getCartTotalValue,
    paymentInfo,
    setPaymentInfo,
    paymentError,
    setPaymentError,
    paymentReceipt,
    paymentIntentId,
    setPaymentIntentId,
    subscriptionId,
    setSubscriptionId,
    customAuth: tempAuthForPurchase, // Custom auth for existing users (not logged in)
    purchaseFlow, // Flow controller instance
    auth,
    tempAuthForPurchase,
    purchaseUserEmail,
    preloadedInvoiceData, // Pre-loaded billing details from beginCheckout
    setPreloadedInvoiceData, // Setter for resetting preloaded data
    // Flow navigation methods
    goToNextStep,
    goToPreviousStep,
    getCurrentFlowStep,
    getFlowContext,
  }

  // Render the store elements
  return (
    <>
      <Cart store={storeInstance} />
      <Checkout store={storeInstance} />
      <PaymentSuccess store={storeInstance} />
      <PaymentError store={storeInstance} />
    </>
  )
}

// Create a proxy to always access the current instance
export const store = new Proxy(
  {},
  {
    get: function (target, prop) {
      if (!storeInstance) {
        console.warn("Store instance not yet initialized")
        return undefined
      }
      return storeInstance[prop]
    },
  }
)
